import { constants } from "node:fs";
import * as fs from "node:fs/promises";
import * as path from "node:path";
import { randomUUID } from "node:crypto";
import { execFile, spawn } from "node:child_process";
import {
  buildArtifactManifest,
  copyArtifactPayload,
  readArtifactManifest,
  verifyArtifactSource,
} from "./companion-artifact.js";

export type CompanionInstallState =
  | { readonly state: "not-installed" }
  | {
      readonly state: "installed";
      readonly version: string;
      readonly executable: string;
      readonly startAtLogin: boolean;
      readonly artifactTreeSha256: string;
    }
  | { readonly state: "needs-repair"; readonly reason: string };

interface InstallManifest {
  readonly schemaVersion: 1;
  readonly version: string;
  readonly platform: "darwin" | "win32";
  readonly architecture: "arm64" | "x64";
  readonly entryRelativePath: string;
  readonly artifactTreeSha256: string;
  readonly ownedTargetRoot: string;
}

interface RuntimeBinding {
  readonly schemaVersion: 1;
  readonly nodeExecutable: string;
  readonly cliEntry: string;
  readonly gatewayOrigin: string;
}

interface StoredPreferences {
  readonly geometry: { readonly edge: "right"; readonly offset: number };
  readonly userHidden: boolean;
  readonly startAtLogin: boolean;
  readonly automaticInstall: boolean;
  readonly lastRelativeRoute: null;
  readonly gatewayOrigin: string;
}

/**
 * What is true of this machine, independent of which build is being installed.
 * Reading and removing an install need only this; installing one also needs a
 * build, which arrives from the release channel rather than from here.
 */
export interface CompanionHostOptions {
  readonly homeDirectory: string;
  readonly platform: NodeJS.Platform;
  readonly architecture: string;
  readonly nodeExecutable: string;
  readonly cliEntry: string;
  readonly gatewayOrigin?: string;
  readonly localAppData?: string;
  readonly launch?: (executable: string) => Promise<void>;
  readonly setStartAtLogin?: (
    executable: string,
    enabled: boolean,
  ) => Promise<void>;
  readonly getStartAtLogin?: (executable: string) => Promise<boolean>;
  readonly removeOwnedPath?: (
    ownedPath: string,
    recursive: boolean,
  ) => Promise<void>;
  readonly copyPayload?: typeof copyArtifactPayload;
  /** Whether the owned executable is in use; the host is asked when this is absent. */
  readonly isRunning?: (executable: string) => Promise<boolean>;
}

/** A host plus the unpacked build to converge it to. */
export interface CompanionInstallOptions extends CompanionHostOptions {
  readonly artifactManifestPath: string;
}

export interface CompanionInstallPaths {
  readonly targetRoot: string;
  readonly installManifest: string;
  readonly runtimeBinding: string;
  readonly preferences: string;
}

function xmlEscape(value: string): string {
  return value
    .replaceAll("&", "&amp;")
    .replaceAll("<", "&lt;")
    .replaceAll(">", "&gt;")
    .replaceAll("\"", "&quot;")
    .replaceAll("'", "&apos;");
}

function commandError(error: unknown, message: string): Error & {
  readonly code?: string | number;
} {
  const wrapped = new Error(message, { cause: error }) as Error & {
    code?: string | number;
  };
  const code = (error as { code?: string | number } | null)?.code;
  if (code !== undefined) wrapped.code = code;
  return wrapped;
}

function execFileAsync(file: string, args: readonly string[]): Promise<void> {
  return new Promise((resolve, reject) => {
    execFile(file, [...args], { windowsHide: true }, (error) => {
      if (error) reject(commandError(error, "Login-item command failed"));
      else resolve();
    });
  });
}

function execFileOutput(file: string, args: readonly string[]): Promise<string> {
  return new Promise((resolve, reject) => {
    execFile(
      file,
      [...args],
      { windowsHide: true },
      (error, stdout) => {
        if (error) reject(commandError(error, "Login-item query failed"));
        else resolve(stdout);
      },
    );
  });
}

export async function setCompanionStartAtLogin(input: {
  readonly homeDirectory: string;
  readonly platform: NodeJS.Platform;
  readonly executable: string;
  readonly enabled: boolean;
  readonly runCommand?: (
    file: string,
    args: readonly string[],
  ) => Promise<string>;
}): Promise<void> {
  if (input.platform === "darwin") {
    const launchAgents = path.join(input.homeDirectory, "Library", "LaunchAgents");
    const registration = path.join(
      launchAgents,
      "com.m8t.companion.plist",
    );
    await assertNotSymlink(registration, "Start-at-login registration");
    if (!input.enabled) {
      await fs.rm(registration, { force: true });
      return;
    }
    await fs.mkdir(launchAgents, { recursive: true, mode: 0o700 });
    const plist =
      "<?xml version=\"1.0\" encoding=\"UTF-8\"?>\n" +
      "<!DOCTYPE plist PUBLIC \"-//Apple//DTD PLIST 1.0//EN\" " +
      "\"http://www.apple.com/DTDs/PropertyList-1.0.dtd\">\n" +
      "<plist version=\"1.0\"><dict>" +
      "<key>Label</key><string>com.m8t.companion</string>" +
      "<key>ProgramArguments</key><array><string>" +
      xmlEscape(input.executable) +
      "</string></array><key>RunAtLoad</key><true/></dict></plist>\n";
    await atomicWriteText(registration, plist, 0o600);
    return;
  }
  if (input.platform === "win32") {
    const key = "HKCU\\Software\\Microsoft\\Windows\\CurrentVersion\\Run";
    const run =
      input.runCommand ??
      (async (file: string, args: readonly string[]) => {
        await execFileAsync(file, args);
        return "";
      });
    if (input.enabled) {
      await run(
        "reg.exe",
        [
            "add",
            key,
            "/v",
            "m8t Companion",
            "/t",
            "REG_SZ",
            "/d",
            `"${input.executable}"`,
            "/f",
        ],
      );
    } else {
      try {
        await run("reg.exe", [
          "query",
          key,
          "/v",
          "m8t Companion",
        ]);
      } catch (error) {
        if ((error as { code?: string | number }).code === 1) return;
        throw error;
      }
      await run("reg.exe", [
        "delete",
        key,
        "/v",
        "m8t Companion",
        "/f",
      ]);
    }
    return;
  }
  throw new Error("Start-at-login is unsupported on this platform");
}

export async function getCompanionStartAtLogin(input: {
  readonly homeDirectory: string;
  readonly platform: NodeJS.Platform;
  readonly executable: string;
}): Promise<boolean> {
  if (input.platform === "darwin") {
    const registration = path.join(
      input.homeDirectory,
      "Library",
      "LaunchAgents",
      "com.m8t.companion.plist",
    );
    try {
      const contents = await readRegularText(registration, 64 * 1024);
      return contents.includes(`<string>${xmlEscape(input.executable)}</string>`);
    } catch (error) {
      if ((error as NodeJS.ErrnoException).code === "ENOENT") return false;
      return false;
    }
  }
  if (input.platform === "win32") {
    try {
      const output = await execFileOutput("reg.exe", [
        "query",
        "HKCU\\Software\\Microsoft\\Windows\\CurrentVersion\\Run",
        "/v",
        "m8t Companion",
      ]);
      return output.includes(`"${input.executable}"`);
    } catch {
      return false;
    }
  }
  return false;
}

const INSTALL_KEYS = [
  "schemaVersion",
  "version",
  "platform",
  "architecture",
  "entryRelativePath",
  "artifactTreeSha256",
  "ownedTargetRoot",
] as const;
const RUNTIME_KEYS = [
  "schemaVersion",
  "nodeExecutable",
  "cliEntry",
  "gatewayOrigin",
] as const;

function isSupportedPlatform(
  platform: NodeJS.Platform,
): platform is "darwin" | "win32" {
  return platform === "darwin" || platform === "win32";
}

function isSupportedArchitecture(
  architecture: string,
): architecture is "arm64" | "x64" {
  return architecture === "arm64" || architecture === "x64";
}

/**
 * Whether the desktop companions exist for the host running this CLI. There is
 * no Linux build, so onboarding asks before it offers rather than installing
 * and then telling a founder that something they never heard of needs repair.
 */
export function companionsSupportHost(
  platform: NodeJS.Platform = process.platform,
  architecture: string = process.arch,
): boolean {
  return isSupportedPlatform(platform) && isSupportedArchitecture(architecture);
}

function assertSupported(options: CompanionHostOptions): void {
  if (
    !isSupportedPlatform(options.platform) ||
    !isSupportedArchitecture(options.architecture)
  ) {
    throw new Error(
      "Desktop companions are supported on macOS and Windows arm64/x64. Run: m8t companion status",
    );
  }
}

export function companionInstallPaths(
  options: CompanionHostOptions,
): CompanionInstallPaths {
  assertSupported(options);
  const companionState = path.join(
    options.homeDirectory,
    ".m8t",
    "companion",
  );
  const targetRoot =
    options.platform === "darwin"
      ? path.join(
          options.homeDirectory,
          "Applications",
          "m8t Companion.app",
        )
      : path.join(
          options.localAppData ??
            path.join(options.homeDirectory, "AppData", "Local"),
          "m8t",
          "companion",
          "app",
        );
  return {
    targetRoot,
    installManifest: path.join(companionState, "install-v1.json"),
    runtimeBinding: path.join(companionState, "runtime-v1.json"),
    preferences: path.join(companionState, "preferences-v1.json"),
  };
}

function exactKeys(
  value: Record<string, unknown>,
  keys: readonly string[],
): boolean {
  return (
    Object.keys(value).length === keys.length &&
    keys.every((key) => Object.hasOwn(value, key))
  );
}

function parseInstallManifest(value: unknown): InstallManifest {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("Install manifest schema is invalid");
  }
  const record = value as Record<string, unknown>;
  if (
    !exactKeys(record, INSTALL_KEYS) ||
    record.schemaVersion !== 1 ||
    typeof record.version !== "string" ||
    (record.platform !== "darwin" && record.platform !== "win32") ||
    (record.architecture !== "arm64" && record.architecture !== "x64") ||
    typeof record.entryRelativePath !== "string" ||
    typeof record.artifactTreeSha256 !== "string" ||
    !/^[a-f0-9]{64}$/u.test(record.artifactTreeSha256) ||
    typeof record.ownedTargetRoot !== "string" ||
    !path.isAbsolute(record.ownedTargetRoot)
  ) {
    throw new Error("Install manifest schema is invalid");
  }
  return record as unknown as InstallManifest;
}

function parseRuntimeBinding(
  value: unknown,
  platform: NodeJS.Platform,
): RuntimeBinding {
  if (!value || typeof value !== "object" || Array.isArray(value)) {
    throw new Error("Runtime binding schema is invalid");
  }
  const record = value as Record<string, unknown>;
  const paths = platform === "win32" ? path.win32 : path.posix;
  if (
    !exactKeys(record, RUNTIME_KEYS) ||
    record.schemaVersion !== 1 ||
    typeof record.nodeExecutable !== "string" ||
    !paths.isAbsolute(record.nodeExecutable) ||
    typeof record.cliEntry !== "string" ||
    !paths.isAbsolute(record.cliEntry) ||
    typeof record.gatewayOrigin !== "string"
  ) {
    throw new Error("Runtime binding schema is invalid");
  }
  return {
    schemaVersion: 1,
    nodeExecutable: record.nodeExecutable,
    cliEntry: record.cliEntry,
    gatewayOrigin: validateGatewayOrigin(record.gatewayOrigin),
  };
}

function validateGatewayOrigin(value: string): string {
  let url: URL;
  try {
    url = new URL(value);
  } catch {
    throw new Error("Companion gateway origin is invalid");
  }
  if (
    url.protocol !== "https:" ||
    url.origin !== value ||
    url.username !== "" ||
    url.password !== "" ||
    url.pathname !== "/" ||
    url.search !== "" ||
    url.hash !== ""
  ) {
    throw new Error("Companion gateway origin must be an HTTPS origin");
  }
  return url.origin;
}

async function assertNotSymlink(filePath: string, kind: string): Promise<void> {
  try {
    if ((await fs.lstat(filePath)).isSymbolicLink()) {
      throw new Error(`${kind} is a symbolic link`);
    }
  } catch (error) {
    if ((error as NodeJS.ErrnoException).code === "ENOENT") return;
    throw error;
  }
}

async function assertOwnedDirectoryChain(
  anchor: string,
  targetDirectory: string,
): Promise<void> {
  const relative = path.relative(anchor, targetDirectory);
  if (
    relative === ".." ||
    relative.startsWith(`..${path.sep}`) ||
    path.isAbsolute(relative)
  ) {
    throw new Error("Companion owned directory escapes its trusted anchor");
  }
  const segments = relative.split(path.sep).filter(Boolean);
  let current = anchor;
  const anchorStatus = await fs.lstat(anchor);
  if (anchorStatus.isSymbolicLink() || !anchorStatus.isDirectory()) {
    throw new Error("Companion owned directory anchor is unsafe");
  }
  for (const segment of segments) {
    current = path.join(current, segment);
    try {
      const status = await fs.lstat(current);
      if (status.isSymbolicLink() || !status.isDirectory()) {
        throw new Error("Companion owned directory contains a symbolic link");
      }
    } catch (error) {
      if ((error as NodeJS.ErrnoException).code === "ENOENT") return;
      throw error;
    }
  }
}

async function assertOwnedParents(
  options: CompanionHostOptions,
  paths: CompanionInstallPaths,
): Promise<void> {
  await assertOwnedDirectoryChain(
    options.homeDirectory,
    path.dirname(paths.installManifest),
  );
  const targetAnchor =
    options.platform === "win32"
      ? options.localAppData ??
        path.join(options.homeDirectory, "AppData", "Local")
      : options.homeDirectory;
  await assertOwnedDirectoryChain(targetAnchor, path.dirname(paths.targetRoot));
}

async function readRegularText(
  filePath: string,
  maxBytes: number,
): Promise<string> {
  const before = await fs.lstat(filePath);
  if (before.isSymbolicLink() || !before.isFile()) {
    throw new Error("Companion state file is not a regular file");
  }
  let handle: fs.FileHandle | undefined;
  try {
    handle = await fs.open(
      filePath,
      constants.O_RDONLY | constants.O_NOFOLLOW,
    );
    const opened = await handle.stat();
    const sameFile =
      (before.ino === 0 && opened.ino === 0) ||
      (before.dev === opened.dev && before.ino === opened.ino);
    if (!opened.isFile() || !sameFile || opened.size > maxBytes) {
      throw new Error("Companion state file is not a bounded regular file");
    }
    return await handle.readFile("utf8");
  } finally {
    await handle?.close().catch(() => undefined);
  }
}

async function readClosedJson(filePath: string): Promise<unknown> {
  return JSON.parse(await readRegularText(filePath, 64 * 1024)) as unknown;
}

async function atomicWriteJson(filePath: string, value: unknown): Promise<void> {
  await atomicWriteText(filePath, `${JSON.stringify(value)}\n`, 0o600);
}

async function atomicWriteText(
  filePath: string,
  contents: string,
  mode: number,
): Promise<void> {
  await assertNotSymlink(filePath, "Companion state file");
  await fs.mkdir(path.dirname(filePath), {
    recursive: true,
    mode: 0o700,
  });
  const temporary = `${filePath}.${randomUUID()}.tmp`;
  try {
    await fs.writeFile(temporary, contents, {
      mode,
      flag: "wx",
    });
    await fs.rename(temporary, filePath);
    await fs.chmod(filePath, mode).catch(() => undefined);
  } finally {
    await fs.rm(temporary, { force: true }).catch(() => undefined);
  }
}

async function realRegularFile(
  filePath: string,
  executable: boolean,
): Promise<string> {
  const real = await fs.realpath(filePath);
  const stat = await fs.stat(real);
  if (!stat.isFile()) throw new Error("Companion launch target is not a file");
  await fs.access(real, executable ? constants.X_OK : constants.R_OK);
  return real;
}

function defaultLaunch(executable: string): Promise<void> {
  const child = spawn(executable, [], {
    detached: true,
    stdio: "ignore",
    windowsHide: true,
  });
  child.unref();
  return Promise.resolve();
}

/**
 * Whether the companion's own executable is in use. Windows will not delete or
 * move the files of a running process and finds that out partway through, so
 * this is asked before anything is touched. A running executable cannot be
 * opened for writing there, while one merely sitting on disk can. macOS has no
 * such rule, so nothing is ever in the way.
 */
async function companionIsRunning(
  platform: NodeJS.Platform,
  executable: string,
): Promise<boolean> {
  if (platform !== "win32") return false;
  let handle: fs.FileHandle | undefined;
  try {
    handle = await fs.open(executable, "r+");
  } catch (error) {
    const code = (error as NodeJS.ErrnoException).code;
    // Nothing on disk holds nothing open.
    if (code === "ENOENT") return false;
    if (code === "EBUSY" || code === "EPERM" || code === "EACCES") return true;
    throw error;
  } finally {
    await handle?.close().catch(() => undefined);
  }
  return false;
}

async function assertCompanionNotRunning(
  options: CompanionHostOptions,
  executable: string,
): Promise<void> {
  const running =
    options.isRunning ??
    ((owned: string) => companionIsRunning(options.platform, owned));
  if (await running(executable)) {
    throw new Error(
      "The desktop companions are running. Quit them from the tray, then run this again.",
    );
  }
}

async function readInstalled(
  options: CompanionHostOptions,
): Promise<{
  readonly paths: CompanionInstallPaths;
  readonly install: InstallManifest;
} | null> {
  const paths = companionInstallPaths(options);
  await assertNotSymlink(paths.installManifest, "Install manifest");
  try {
    return {
      paths,
      install: parseInstallManifest(
        await readClosedJson(paths.installManifest),
      ),
    };
  } catch (error) {
    if ((error as NodeJS.ErrnoException).code === "ENOENT") return null;
    throw error;
  }
}

export async function statusCompanion(
  options: CompanionHostOptions,
): Promise<CompanionInstallState> {
  assertSupported(options);
  const resolvedPaths = companionInstallPaths(options);
  try {
    await assertOwnedParents(options, resolvedPaths);
  } catch (error) {
    return {
      state: "needs-repair",
      reason: error instanceof Error ? error.message : "Unsafe owned directory",
    };
  }
  let installed: Awaited<ReturnType<typeof readInstalled>>;
  try {
    installed = await readInstalled(options);
  } catch (error) {
    return {
      state: "needs-repair",
      reason: error instanceof Error ? error.message : "Invalid install manifest",
    };
  }
  if (!installed) return { state: "not-installed" };
  const { paths, install } = installed;
  if (
    install.platform !== options.platform ||
    install.architecture !== options.architecture ||
    install.ownedTargetRoot !== paths.targetRoot
  ) {
    return {
      state: "needs-repair",
      reason: "Install manifest does not own the fixed companion target",
    };
  }
  try {
    await assertNotSymlink(paths.targetRoot, "Install target");
    const actual = await buildArtifactManifest(paths.targetRoot, {
      platform: install.platform,
      architecture: install.architecture,
      version: install.version,
      entryRelativePath: install.entryRelativePath,
    });
    if (actual.treeSha256 !== install.artifactTreeSha256) {
      throw new Error("Installed artifact digest does not match");
    }
    const runtime = parseRuntimeBinding(
      await readClosedJson(paths.runtimeBinding),
      options.platform,
    );
    await Promise.all([
      realRegularFile(runtime.nodeExecutable, options.platform !== "win32"),
      realRegularFile(runtime.cliEntry, false),
    ]);
    const executable = path.join(
      paths.targetRoot,
      ...install.entryRelativePath.split("/"),
    );
    await realRegularFile(executable, options.platform !== "win32");
    return {
      state: "installed",
      version: install.version,
      executable,
      startAtLogin: await (
        options.getStartAtLogin ??
        ((ownedExecutable: string) =>
          getCompanionStartAtLogin({
            homeDirectory: options.homeDirectory,
            platform: options.platform,
            executable: ownedExecutable,
          }))
      )(executable),
      artifactTreeSha256: install.artifactTreeSha256,
    };
  } catch (error) {
    return {
      state: "needs-repair",
      reason: error instanceof Error ? error.message : "Installed artifact is invalid",
    };
  }
}

async function snapshotFile(filePath: string): Promise<Buffer | null> {
  try {
    return Buffer.from(await readRegularText(filePath, 64 * 1024), "utf8");
  } catch (error) {
    if ((error as NodeJS.ErrnoException).code === "ENOENT") return null;
    throw error;
  }
}

async function restoreFile(filePath: string, bytes: Buffer | null): Promise<void> {
  if (bytes === null) {
    await fs.rm(filePath, { force: true });
  } else {
    await atomicWriteJson(filePath, JSON.parse(bytes.toString("utf8")) as unknown);
  }
}

async function converge(
  options: CompanionInstallOptions,
  force: boolean,
): Promise<CompanionInstallState> {
  assertSupported(options);
  const paths = companionInstallPaths(options);
  await assertOwnedParents(options, paths);
  const artifactManifest = await readArtifactManifest(
    options.artifactManifestPath,
  );
  if (
    artifactManifest.platform !== options.platform ||
    artifactManifest.architecture !== options.architecture
  ) {
    throw new Error("Companion artifact does not match this OS and architecture");
  }
  const payloadRoot = path.join(
    path.dirname(options.artifactManifestPath),
    "payload",
  );
  await verifyArtifactSource(payloadRoot, artifactManifest);
  for (const boundary of [
    paths.targetRoot,
    paths.installManifest,
    paths.runtimeBinding,
    paths.preferences,
  ]) {
    await assertNotSymlink(boundary, "Companion owned boundary");
  }
  const current = await statusCompanion(options);
  const priorInstalled = await readInstalled(options);
  if (
    !force &&
    current.state === "installed" &&
    current.version === artifactManifest.version &&
    current.artifactTreeSha256 === artifactManifest.treeSha256 &&
    current.startAtLogin
  ) {
    return current;
  }
  // Converging over a running companion fails at the rename and rolls back, so
  // nothing is damaged — but only after the whole payload has been copied, and
  // it reports the rename rather than the reason. Asked here instead.
  if (priorInstalled) {
    await assertCompanionNotRunning(
      options,
      path.join(
        priorInstalled.paths.targetRoot,
        ...priorInstalled.install.entryRelativePath.split("/"),
      ),
    );
  }
  if (current.state === "not-installed") {
    try {
      await fs.lstat(paths.targetRoot);
      throw new Error(
        "The fixed companion target exists without an owned install manifest",
      );
    } catch (error) {
      if ((error as NodeJS.ErrnoException).code !== "ENOENT") throw error;
    }
  }

  const stage = `${paths.targetRoot}.m8t-stage-${randomUUID()}`;
  const backup = `${paths.targetRoot}.m8t-backup-${randomUUID()}`;
  const copy = options.copyPayload ?? copyArtifactPayload;
  await fs.mkdir(path.dirname(paths.targetRoot), {
    recursive: true,
    mode: 0o700,
  });
  let movedPrior = false;
  let installedStage = false;
  let loginChanged = false;
  let priorLoginState = false;
  let priorLoginExecutable: string | undefined;
  const snapshots = await Promise.all([
    snapshotFile(paths.installManifest),
    snapshotFile(paths.runtimeBinding),
    snapshotFile(paths.preferences),
  ]);
  try {
    await copy(payloadRoot, stage, artifactManifest);
    try {
      await fs.rename(paths.targetRoot, backup);
      movedPrior = true;
    } catch (error) {
      if ((error as NodeJS.ErrnoException).code !== "ENOENT") throw error;
    }
    await fs.rename(stage, paths.targetRoot);
    installedStage = true;

    const nodeExecutable = await realRegularFile(
      options.nodeExecutable,
      options.platform !== "win32",
    );
    const cliEntry = await realRegularFile(options.cliEntry, false);
    if (options.gatewayOrigin === undefined) {
      throw new Error("Companion gateway origin is required for installation");
    }
    const gatewayOrigin = validateGatewayOrigin(options.gatewayOrigin);
    const install: InstallManifest = {
      schemaVersion: 1,
      version: artifactManifest.version,
      platform: artifactManifest.platform,
      architecture: artifactManifest.architecture,
      entryRelativePath: artifactManifest.entryRelativePath,
      artifactTreeSha256: artifactManifest.treeSha256,
      ownedTargetRoot: paths.targetRoot,
    };
    const runtime: RuntimeBinding = {
      schemaVersion: 1,
      nodeExecutable,
      cliEntry,
      gatewayOrigin,
    };
    const preferences: StoredPreferences = {
      geometry: { edge: "right", offset: 0.5 },
      userHidden: false,
      startAtLogin: true,
      automaticInstall: false,
      lastRelativeRoute: null,
      gatewayOrigin,
    };
    await atomicWriteJson(paths.installManifest, install);
    // How the companion finds the CLI it came with. Nothing else can tell it:
    // a windowed app inherits no shell PATH, and on Windows the launcher is a
    // batch shim in a per-user directory. Moving or rewriting this file takes
    // the companion offline until the next repair.
    await atomicWriteJson(paths.runtimeBinding, runtime);
    await atomicWriteJson(paths.preferences, preferences);
    parseInstallManifest(await readClosedJson(paths.installManifest));
    parseRuntimeBinding(
      await readClosedJson(paths.runtimeBinding),
      options.platform,
    );

    const executable = path.join(
      paths.targetRoot,
      ...artifactManifest.entryRelativePath.split("/"),
    );
    await realRegularFile(executable, options.platform !== "win32");
    const getLogin =
      options.getStartAtLogin ??
      ((ownedExecutable: string) =>
        getCompanionStartAtLogin({
          homeDirectory: options.homeDirectory,
          platform: options.platform,
          executable: ownedExecutable,
        }));
    priorLoginExecutable = priorInstalled
      ? path.join(
          priorInstalled.paths.targetRoot,
          ...priorInstalled.install.entryRelativePath.split("/"),
        )
      : executable;
    priorLoginState = await getLogin(priorLoginExecutable);
    await (options.setStartAtLogin ?? (() => Promise.resolve()))(
      executable,
      true,
    );
    loginChanged = true;
    await (options.launch ?? defaultLaunch)(executable);
    await fs.rm(backup, { recursive: true, force: true });
    return {
      state: "installed",
      version: artifactManifest.version,
      executable,
      startAtLogin: true,
      artifactTreeSha256: artifactManifest.treeSha256,
    };
  } catch (error) {
    if (loginChanged) {
      await (
        options.setStartAtLogin ??
        (() => Promise.resolve())
      )(priorLoginExecutable ?? paths.targetRoot, priorLoginState).catch(
        () => undefined,
      );
    }
    await fs.rm(stage, { recursive: true, force: true }).catch(() => undefined);
    if (installedStage) {
      await fs.rm(paths.targetRoot, {
        recursive: true,
        force: true,
      }).catch(() => undefined);
    }
    if (movedPrior) {
      await fs.rename(backup, paths.targetRoot).catch(() => undefined);
    }
    await Promise.all([
      restoreFile(paths.installManifest, snapshots[0]),
      restoreFile(paths.runtimeBinding, snapshots[1]),
      restoreFile(paths.preferences, snapshots[2]),
    ]).catch(() => undefined);
    throw error;
  }
}

export async function installCompanion(
  options: CompanionInstallOptions,
): Promise<CompanionInstallState> {
  return converge(options, false);
}

export async function repairCompanion(
  options: CompanionInstallOptions,
): Promise<CompanionInstallState> {
  return converge(options, true);
}

export async function uninstallCompanion(
  options: CompanionHostOptions,
): Promise<CompanionInstallState> {
  assertSupported(options);
  const expectedPaths = companionInstallPaths(options);
  await assertOwnedParents(options, expectedPaths);
  const installed = await readInstalled(options);
  if (!installed) return { state: "not-installed" };
  const { paths, install } = installed;
  if (install.ownedTargetRoot !== paths.targetRoot) {
    throw new Error("Refusing to remove a non-owned companion target");
  }
  const executable = path.join(
    paths.targetRoot,
    ...install.entryRelativePath.split("/"),
  );
  // Before the login item and before the tree: a refusal here leaves the
  // install exactly as it was, which a half-finished removal does not.
  await assertCompanionNotRunning(options, executable);
  await (options.setStartAtLogin ?? (() => Promise.resolve()))(executable, false);
  const remove =
    options.removeOwnedPath ??
    (async (ownedPath: string, recursive: boolean) => {
      await fs.rm(ownedPath, { recursive, force: true });
    });
  await remove(paths.targetRoot, true);
  await remove(paths.installManifest, false);
  await remove(paths.runtimeBinding, false);
  await remove(paths.preferences, false);
  return { state: "not-installed" };
}
